import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { 
  Package, FileCheck, CheckCircle, Inbox, Calendar, Sun, Cog, 
  Users, Building2, Activity, TrendingUp, AlertTriangle, Clock,
  Factory, Image, FileText, Zap, BarChart3, CalendarRange, X
} from 'lucide-react';
import { useGetDashboardStats } from '../../hooks/useDashboard';
import { useTimeAlerts } from '../../hooks/useTimeAlerts';
import { getMexicoDateString } from '../../lib/mexicoDate';

type RangeKey = 'all' | '7d' | '15d' | '30d' | 'custom';

function toISODate(d: Date) {
  return getMexicoDateString(d);
}

function computeRange(range: RangeKey, customFrom: string, customTo: string) {
  if (range === 'all') return { from: undefined, to: undefined };
  if (range === 'custom') return { from: customFrom || undefined, to: customTo || undefined };
  const to = new Date();
  const from = new Date();
  const days = range === '7d' ? 7 : range === '15d' ? 15 : 30;
  from.setDate(from.getDate() - days);
  return { from: toISODate(from), to: toISODate(to) };
}

export default function Dashboard() {
  const { t } = useTranslation();

  const [activeRange, setActiveRange] = useState<RangeKey>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const dateRange = useMemo(
    () => computeRange(activeRange, customFrom, customTo),
    [activeRange, customFrom, customTo]
  );

  const { data: stats, isLoading, error } = useGetDashboardStats(dateRange);
  const { data: timeAlertsData } = useTimeAlerts();
  const exceededAlerts = (timeAlertsData?.alerts || []).filter(a => a.isExceeded);
  const exceededProcessAlerts = (timeAlertsData?.processAlerts || []).filter(a => a.isExceeded);

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="text-center py-8 text-red-600">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
        <p>Error al cargar el dashboard</p>
      </div>
    );
  }

  const rangeLabel: Record<RangeKey, string> = {
    all: 'Todo el tiempo', '7d': '7 días', '15d': '15 días', '30d': '1 mes', custom: 'Personalizado',
  };

  const handlePreset = (r: RangeKey) => {
    setActiveRange(r);
    if (r !== 'custom') setShowCustom(false);
    else setShowCustom(true);
  };

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
        <div className="relative z-10 flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-2">
                <Zap className="h-3 w-3 mr-1" /> Sistema activo
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                {t('app.dashboard')}
              </h2>
              <p className="text-gray-600 mt-1">
                Resumen ejecutivo de operaciones de manufactura
              </p>
            </div>
            <div className="hidden md:flex items-center gap-4 text-sm text-gray-500 flex-shrink-0">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.productionOrders.ordersThisWeek}</div>
                <div>Esta semana</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{stats.productionOrders.ordersThisMonth}</div>
                <div>Este mes</div>
              </div>
            </div>
          </div>

          {/* Date range filter bar */}
          <div className="flex flex-wrap items-center gap-2">
            <CalendarRange className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span className="text-xs font-medium text-gray-500 mr-1">Período:</span>
            {(['all', '7d', '15d', '30d', 'custom'] as RangeKey[]).map(r => (
              <button
                key={r}
                onClick={() => handlePreset(r)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                  activeRange === r
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
                }`}
              >
                {rangeLabel[r]}
              </button>
            ))}
            {activeRange !== 'all' && (
              <button
                onClick={() => { setActiveRange('all'); setShowCustom(false); setCustomFrom(''); setCustomTo(''); }}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors ml-1"
                title="Limpiar filtro"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {activeRange !== 'all' && activeRange !== 'custom' && dateRange.from && (
              <span className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 ml-1">
                {dateRange.from} → {dateRange.to}
              </span>
            )}
          </div>

          {/* Custom date pickers */}
          {showCustom && (
            <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-blue-200 px-4 py-3 shadow-sm">
              <span className="text-xs font-medium text-gray-500">Desde:</span>
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <span className="text-xs font-medium text-gray-500">Hasta:</span>
              <input
                type="date"
                value={customTo}
                max={toISODate(new Date())}
                onChange={e => setCustomTo(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              {customFrom && customTo && (
                <span className="text-xs text-green-600 font-medium">
                  {Math.round((new Date(customTo).getTime() - new Date(customFrom).getTime()) / 86400000) + 1} días seleccionados
                </span>
              )}
            </div>
          )}
        </div>
        <div className="absolute top-2 right-4 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-2xl"></div>
      </div>

      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-7">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-gray-50 to-gray-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Package className="h-5 w-5 text-gray-500" />
              <span className="text-2xl font-bold text-gray-700">{stats.productionOrders.total}</span>
            </div>
            <p className="text-xs font-medium text-gray-600">Total Órdenes</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <FileCheck className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold text-blue-700">{stats.productionOrders.created}</span>
            </div>
            <p className="text-xs font-medium text-blue-600">Creadas</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-green-700">{stats.productionOrders.authorized}</span>
            </div>
            <p className="text-xs font-medium text-green-600">Autorizadas</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-yellow-50 to-yellow-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Inbox className="h-5 w-5 text-yellow-600" />
              <span className="text-2xl font-bold text-yellow-700">{stats.productionOrders.backlog}</span>
            </div>
            <p className="text-xs font-medium text-yellow-600">Backlog</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-cyan-50 to-cyan-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="h-5 w-5 text-cyan-600" />
              <span className="text-2xl font-bold text-cyan-700">{stats.productionOrders.planned}</span>
            </div>
            <p className="text-xs font-medium text-cyan-600">Planeadas</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-orange-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Sun className="h-5 w-5 text-orange-500" />
              <span className="text-2xl font-bold text-orange-700">{stats.productionOrders.today}</span>
            </div>
            <p className="text-xs font-medium text-orange-600">Para Hoy</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-indigo-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Cog className="h-5 w-5 text-indigo-500 animate-spin" style={{ animationDuration: '3s' }} />
              <span className="text-2xl font-bold text-indigo-700">{stats.productionOrders.inProduction}</span>
            </div>
            <p className="text-xs font-medium text-indigo-600">En Producción</p>
          </CardContent>
        </Card>
      </div>

      {/* Time exceeded alerts panel — orders */}
      {exceededAlerts.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <span className="animate-pulse text-xl">⏰</span>
            <h3 className="font-bold text-red-700 text-base">
              {exceededAlerts.length} {exceededAlerts.length === 1 ? 'orden excedió' : 'órdenes excedieron'} el tiempo estimado de producción
            </h3>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {exceededAlerts.map(a => (
              <div key={a.orderId} className="bg-white border border-red-200 rounded-lg px-3 py-2 space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-sm text-gray-800">#{a.orderNumber}</span>
                  <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">+{a.overageFormatted}</span>
                </div>
                {a.machineName && <div className="text-xs text-gray-500">📍 {a.machineName}</div>}
                {a.customerName && <div className="text-xs text-gray-500 truncate">👥 {a.customerName}</div>}
                <div className="flex gap-3 text-xs text-gray-600">
                  <span>Estimado: <strong>{a.estimatedFormatted}</strong></span>
                  <span>Transcurrido: <strong className="text-red-600">{a.elapsedFormatted}</strong></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Process-level time exceeded alerts panel */}
      {exceededProcessAlerts.length > 0 && (
        <div className="bg-orange-50 border border-orange-300 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <span className="animate-pulse text-xl">⚙️</span>
            <h3 className="font-bold text-orange-700 text-base">
              {exceededProcessAlerts.length} {exceededProcessAlerts.length === 1 ? 'proceso excedió' : 'procesos excedieron'} el tiempo estimado
            </h3>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {exceededProcessAlerts.map(a => (
              <div key={a.routeId} className="bg-white border border-orange-200 rounded-lg px-3 py-2 space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-sm text-gray-800">#{a.orderNumber}</span>
                  <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">+{a.overageFormatted}</span>
                </div>
                <div className="text-xs text-gray-700 font-medium truncate">⚙️ {a.process}</div>
                {a.machineName && <div className="text-xs text-gray-500">📍 {a.machineName}</div>}
                {a.customerName && <div className="text-xs text-gray-500 truncate">👥 {a.customerName}</div>}
                <div className="flex gap-3 text-xs text-gray-600">
                  <span>Estimado: <strong>{a.estimatedFormatted}</strong></span>
                  <span>Transcurrido: <strong className="text-orange-600">{a.elapsedFormatted}</strong></span>
                </div>
                {a.tiempoPreparacionMin > 0 && (
                  <div className="text-xs text-gray-400">Prep.: {a.tiempoPreparacionMin} min · Carrera: {a.velocidadMPM > 0 ? `${a.velocidadMPM} m/min` : '—'}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <TrendingUp className="h-4 w-4" /> Cantidades Planificadas
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{formatNumber(stats.quantities.planned)}</div>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Producto en Proceso</span>
                <span className="font-medium text-blue-600">{formatNumber(stats.quantities.proceso)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Producto en Merma</span>
                <span className="font-medium text-red-600">{formatNumber(stats.quantities.merma)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Producto Terminado</span>
                <span className="font-medium text-green-600">{formatNumber(stats.quantities.productoTerminado)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <AlertTriangle className="h-4 w-4" /> Por Prioridad
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm text-gray-600">Alta</span>
                </div>
                <span className="text-lg font-bold text-red-600">{stats.priorities.high}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span className="text-sm text-gray-600">Media</span>
                </div>
                <span className="text-lg font-bold text-yellow-600">{stats.priorities.medium}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-gray-600">Baja</span>
                </div>
                <span className="text-lg font-bold text-green-600">{stats.priorities.low}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <Activity className="h-4 w-4" /> Eventos del Sistema
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.events.total}</div>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Hoy</span>
                <span className="font-medium text-blue-600">{stats.events.today}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Esta semana</span>
                <span className="font-medium text-purple-600">{stats.events.thisWeek}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Con evidencia</span>
                <span className="font-medium text-green-600">{stats.events.withEvidence}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <Factory className="h-4 w-4" /> Manufactura
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.manufacturing.totalRoutes}</div>
            <p className="text-xs text-gray-500 mb-2">Rutas de fabricación</p>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 flex items-center gap-1"><Image className="h-3 w-3" /> Imágenes</span>
                <span className="font-medium">{stats.manufacturing.imageEvidence}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 flex items-center gap-1"><FileText className="h-3 w-3" /> Textos</span>
                <span className="font-medium">{stats.manufacturing.textEvidence}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <Building2 className="h-4 w-4" /> Top 5 Clientes
            </div>
          </CardHeader>
          <CardContent>
            {stats.topClients.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Sin datos de clientes</p>
            ) : (
              <div className="space-y-2">
                {stats.topClients.map((client, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-400 w-4">{idx + 1}</span>
                      <span className="text-sm text-gray-700 truncate max-w-[120px]" title={client.name}>
                        {client.name}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-blue-600">{client.orders}</span>
                      <span className="text-xs text-gray-400 ml-1">órdenes</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <BarChart3 className="h-4 w-4" /> Carga por Equipo
            </div>
          </CardHeader>
          <CardContent>
            {stats.equipmentLoad.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Sin equipos asignados</p>
            ) : (
              <div className="space-y-2">
                {stats.equipmentLoad.map((eq, idx) => (
                  <div key={idx} className="py-1 border-b border-gray-100 last:border-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-700 truncate max-w-[120px]" title={eq.name}>
                        {eq.name}
                      </span>
                      <span className="text-sm font-bold text-gray-900">{eq.total}</span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      <span className="text-green-600">{eq.authorized} auth</span>
                      <span className="text-indigo-600">{eq.inProduction} prod</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <Clock className="h-4 w-4" /> Actividad Reciente
            </div>
          </CardHeader>
          <CardContent>
            {stats.recentActivity.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Sin actividad reciente</p>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {stats.recentActivity.map((activity, idx) => (
                  <div key={idx} className="py-1 border-b border-gray-100 last:border-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-blue-600 truncate">{activity.orderNumber}</p>
                        <p className="text-xs text-gray-600 truncate" title={activity.description}>
                          {activity.description}
                        </p>
                      </div>
                      <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">
                        {formatDate(activity.timestamp)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-emerald-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 font-medium">Clientes Registrados</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.catalogs.clients}</p>
              </div>
              <Building2 className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-violet-50 to-violet-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-violet-600 font-medium">Equipos Activos</p>
                <p className="text-2xl font-bold text-violet-700">{stats.catalogs.activeEquipment}</p>
                <p className="text-xs text-violet-500">de {stats.catalogs.equipment} total</p>
              </div>
              <Factory className="h-8 w-8 text-violet-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-rose-50 to-rose-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-rose-600 font-medium">Usuarios Activos</p>
                <p className="text-2xl font-bold text-rose-700">{stats.users.active}</p>
                <p className="text-xs text-rose-500">de {stats.users.total} total</p>
              </div>
              <Users className="h-8 w-8 text-rose-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 font-medium">Órdenes Completadas</p>
                <p className="text-2xl font-bold text-amber-700">{stats.productionOrders.completed}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
